/*
 * tss-variant-kit.js — TSS compact PDP offer, 9 Oct 2026
 *
 * Drives snippets/tss-pdp-offer.liquid. Dawn's real radios stay in the DOM
 * (off-screen) and remain the source of truth for variant id, price, media and
 * availability; this file proxies them behind the compact UI.
 *
 * Combined kit: "Book Only" pairs with "Skip Candle", "Complete Creative Kit"
 * pairs with a scent. The retired combinations are unreachable, including via
 * a ?variant= deep link.
 *
 * One CTA, which routes instead of multiplying:
 *   tier 1   -> submits the product form (Dawn's button, hidden by CSS)
 *   tier 2-4 -> Bundle Builder with ?tier= and ?seed= preset
 *
 * The price header is lifted out of the block and placed where Dawn's price
 * was, since Dawn's price, stock badge, tax line and quantity stepper are all
 * hidden. Its child nodes are CACHED BEFORE THE MOVE: once relocated the
 * header is no longer a descendant of the offer block, so re-querying it from
 * there finds nothing and the price renders blank.
 */
(() => {
  const ROLE = {
    a4: 'size-a4',
    a3: 'size-a3',
    kitNone: 'kit-none',
    kitFull: 'kit-full',
    candleSkip: 'candle-skip',
    candleScent: 'candle-scent',
  };

  let anchored = false;

  const init = (offer) => {
    if (!offer || offer.dataset.tssBound === 'true') return;

    const scope = offer.closest('.shopify-section') || document;
    const vs =
      scope.querySelector('variant-selects.tss-combined-kit') ||
      document.querySelector('variant-selects.tss-combined-kit');
    if (!vs) return;

    const json = vs.querySelector('[data-spectrum-variants]');
    if (!json) return;

    let variants = [];
    try {
      variants = JSON.parse(json.textContent);
    } catch (e) {
      return;
    }
    if (!variants.length) return;

    offer.dataset.tssBound = 'true';
    if (scope.classList) scope.classList.add('tss-has-offer');

    const currency = vs.dataset.currencyCode || 'INR';
    const sizePos = Number(vs.dataset.tssSizePosition);
    const kitPos = Number(vs.dataset.tssKitPosition);
    const candlePos = Number(vs.dataset.tssCandlePosition);

    const money = (paise) => {
      const rupees = Math.round(Number(paise || 0) / 100);
      try {
        return new Intl.NumberFormat('en-IN', {
          style: 'currency',
          currency,
          maximumFractionDigits: 0,
        }).format(rupees);
      } catch (e) {
        return '₹' + rupees;
      }
    };

    const radios = (role) =>
      [...vs.querySelectorAll('input[type="radio"][data-tss-role="' + role + '"]')];
    const radio = (role) => radios(role)[0] || null;

    const scents = radios(ROLE.candleScent);
    const skip = radio(ROLE.candleSkip);
    const firstScent = () =>
      scents.find((i) => !i.classList.contains('disabled')) || scents[0] || null;

    const find = (wanted) =>
      variants.find(
        (v) =>
          v.options &&
          v.options.every((x, i) => wanted[i] === undefined || String(x) === String(wanted[i]))
      );

    const selection = () => {
      const s = [];
      vs.querySelectorAll('input[type="radio"]:checked[data-spectrum-position]').forEach((i) => {
        s[Number(i.dataset.spectrumPosition)] = i.value;
      });
      return s;
    };

    const kitOn = () => radios(ROLE.kitFull).some((i) => i.checked);
    const a3On = () => {
      const r = radio(ROLE.a3);
      return Boolean(r && r.checked);
    };

    const priceFor = (opts) => {
      const w = selection();
      if (opts.size !== undefined) w[sizePos] = opts.size;
      if (opts.kit !== undefined) w[kitPos] = opts.kit;
      const wantsKit =
        opts.kit !== undefined ? opts.kit === (radio(ROLE.kitFull) || {}).value : kitOn();
      const scent = firstScent();
      w[candlePos] = wantsKit ? (scent ? scent.value : undefined) : skip ? skip.value : undefined;
      const v = find(w);
      return v ? Number(v.price) : null;
    };

    const state = { tier: 1 };

    const setRadio = (input) => {
      if (!input || input.checked) return false;
      input.checked = true;
      return true;
    };

    const reconcile = () => {
      if (kitOn()) {
        if (skip && skip.checked) return setRadio(firstScent());
        if (scents.length && !scents.some((i) => i.checked)) return setRadio(firstScent());
        return false;
      }
      if (scents.some((i) => i.checked)) return setRadio(skip);
      if (skip && !skip.checked) return setRadio(skip);
      return false;
    };

    const commit = () => vs.dispatchEvent(new Event('change', { bubbles: true }));

    const chooseRole = (role) => {
      const target = radio(role);
      if (!target) return;
      setRadio(target);
      reconcile();
      commit();
    };

    /* ---------- price header: cache children, THEN relocate ---------- */

    const head = offer.querySelector('[data-tss-pricehead]');
    const priceNode = head ? head.querySelector('[data-tss-price]') : null;
    const summaryNode = head ? head.querySelector('[data-tss-summary-line]') : null;

    if (head && head.dataset.tssPlaced !== 'true') {
      const dawnPrice = scope.querySelector('[id^="price-"]');
      if (dawnPrice && dawnPrice.parentNode) {
        head.dataset.tssPlaced = 'true';
        dawnPrice.parentNode.insertBefore(head, dawnPrice);
      }
    }

    /* ---------- wiring ---------- */

    offer.querySelectorAll('[data-tss-chip]').forEach((btn) => {
      btn.addEventListener('click', () => chooseRole(btn.dataset.tssChip));
    });

    offer.querySelectorAll('[data-tss-tier]').forEach((btn) => {
      btn.addEventListener('click', () => {
        state.tier = Number(btn.dataset.tssTier);
        render();
      });
    });

    const scentSelect = offer.querySelector('[data-tss-scent-select]');
    if (scentSelect) {
      scentSelect.innerHTML = '';
      scents.forEach((s) => {
        const label = vs.querySelector('label[for="' + CSS.escape(s.id) + '"] .spectrum-card-title');
        const opt = document.createElement('option');
        opt.value = s.value;
        opt.textContent = label ? label.textContent.trim() : s.value;
        scentSelect.appendChild(opt);
      });
      scentSelect.addEventListener('change', () => {
        const match = scents.find((s) => s.value === scentSelect.value);
        if (match) {
          setRadio(match);
          commit();
        }
      });
    }

    const cta = offer.querySelector('[data-tss-cta]');
    if (cta) {
      cta.addEventListener('click', () => {
        if (state.tier > 1) {
          const url = new URL(offer.dataset.builderUrl, window.location.origin);
          url.searchParams.set('tier', String(state.tier));
          url.searchParams.set('seed', offer.dataset.productHandle);
          if (kitOn()) url.searchParams.set('extra', 'both');
          window.location.href = url.toString();
          return;
        }
        const submit = scope.querySelector('.product-form__submit');
        if (submit) submit.click();
      });
    }

    /* ---------- render ---------- */

    const BUNDLE_BASE = { 2: 139900, 3: 179900, 4: 219900 };

    const render = () => {
      const bundling = state.tier > 1;

      const setChip = (role, text, on) => {
        const btn = offer.querySelector('[data-tss-chip="' + role + '"]');
        const node = offer.querySelector('[data-tss-chip-price="' + role + '"]');
        if (node) node.textContent = text;
        if (btn) {
          btn.classList.toggle('is-active', on);
          btn.setAttribute('aria-checked', String(on));
        }
      };

      const a4Val = (radio(ROLE.a4) || {}).value;
      const a3Val = (radio(ROLE.a3) || {}).value;
      const a4p = priceFor({ size: a4Val });
      const a3p = priceFor({ size: a3Val });
      setChip(ROLE.a4, a4p === null ? '' : 'Included', !a3On());
      setChip(ROLE.a3, a4p === null || a3p === null ? '' : '+ ' + money(a3p - a4p), a3On());

      const bookOnly = priceFor({ kit: (radio(ROLE.kitNone) || {}).value });
      const withKit = priceFor({ kit: (radio(ROLE.kitFull) || {}).value });
      setChip(ROLE.kitNone, bookOnly === null ? '' : money(bookOnly), !kitOn());
      setChip(ROLE.kitFull, withKit === null ? '' : money(withKit), kitOn());

      const setBlock = offer.querySelector('[data-tss-set-block]');
      if (setBlock) setBlock.hidden = bundling;

      const scentWrap = offer.querySelector('[data-tss-scent-compact]');
      if (scentWrap) scentWrap.hidden = !kitOn();
      if (scentSelect) {
        const checked = scents.find((s) => s.checked);
        if (checked) scentSelect.value = checked.value;
      }

      offer.querySelectorAll('[data-tss-tier]').forEach((btn) => {
        const on = Number(btn.dataset.tssTier) === state.tier;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-checked', String(on));
      });

      const note = offer.querySelector('[data-tss-note]');
      const sizeLabel = a3On() ? 'A3' : 'A4';

      if (bundling) {
        const base = BUNDLE_BASE[state.tier];
        if (cta) cta.textContent = 'Build my ' + state.tier + '-book bundle · ' + money(base);
        if (priceNode) priceNode.textContent = money(base);
        if (summaryNode) {
          summaryNode.textContent = state.tier + ' books · choose your titles in the builder';
        }
        if (note) {
          note.hidden = false;
          note.textContent = 'Pick your titles, sizes and kit inside the Bundle Builder.';
        }
      } else {
        const current = find(selection());
        const amount = current ? Number(current.price) : null;
        if (cta) cta.textContent = amount === null ? 'Add to cart' : 'Add to cart · ' + money(amount);
        if (priceNode) priceNode.textContent = amount === null ? '' : money(amount);
        if (summaryNode) {
          summaryNode.textContent =
            sizeLabel + ' · ' + (kitOn() ? 'Book + Complete Creative Kit' : 'Book Only');
        }
        if (note) note.hidden = true;
      }
    };

    const update = () => {
      if (reconcile()) {
        commit();
        return;
      }
      render();
    };

    vs.addEventListener('change', () => setTimeout(update, 0));

    /*
     * Open on the lowest entry price. Shopify's default selected variant is
     * the 1,599 kit, which made every product page read as a 1,599 product
     * and buried the 899 anchor the ladder is built on. A customer who
     * arrived on a specific ?variant= link keeps their choice.
     */
    const deepLinked = new URLSearchParams(window.location.search).has('variant');
    if (!anchored && !deepLinked && kitOn()) {
      anchored = true;
      setRadio(radio(ROLE.kitNone));
      reconcile();
      commit();
    } else {
      anchored = true;
      update();
    }
  };

  const boot = () => {
    document.documentElement.classList.add('tss-js-ready');
    document.querySelectorAll('[data-tss-offer]').forEach(init);
  };

  let timer = null;
  const schedule = () => {
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      boot();
    }, 60);
  };

  const start = () => {
    boot();
    if (window.MutationObserver) {
      new MutationObserver(schedule).observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', boot);
})();